import { Container, Sprite } from "pixi.js";
import type {
  ProjectileSnapshot,
  ShipSnapshot,
} from "@frontier/protocol/contracts";
import type { Point } from "./geometry";
import type { CombatViewport } from "./combat-vfx";
import { laserTexture } from "./laser-textures";
import type { RenderedCombatShips } from "./combat-ship-pose";

export const LASER_BEAM_LIMITS = {
  afterglows: 64,
  spares: 128,
  afterglowMs: 180,
  staleAfterMs: 250,
  staleFadeMs: 120,
} as const;
interface OwnerIdentity {
  hullId: ShipSnapshot["hullId"];
  sectorId: ShipSnapshot["sectorId"];
  flightId: string | null;
  x: number;
  y: number;
  radius: number;
}
interface LaserView {
  container: Container;
  channel: Sprite;
  muzzle: Sprite;
  shot: ProjectileSnapshot;
  previous: Point;
  start: Point;
  end: Point;
  owner: OwnerIdentity | null;
  attached: boolean;
  active: boolean;
  expiresAt: number;
}
/** Segment bounds keep a crossing beam visible even when both ends are off screen. */
export function laserBeamVisible(
  start: Point,
  end: Point,
  viewport?: CombatViewport,
): boolean {
  if (!Number.isFinite(start.x + start.y + end.x + end.y)) return false;
  return (
    !viewport ||
    (Math.max(start.x, end.x) + 8 >= viewport.x &&
      Math.min(start.x, end.x) - 8 <= viewport.x + viewport.width &&
      Math.max(start.y, end.y) + 8 >= viewport.y &&
      Math.min(start.y, end.y) - 8 <= viewport.y + viewport.height)
  );
}
export class LaserBeams {
  readonly container = new Container({ label: "laser-beams" });
  private readonly channelTexture = laserTexture("channel");
  private readonly muzzleTexture = laserTexture("muzzle");
  private readonly views = new Map<string, LaserView>();
  private readonly spares: LaserView[] = [];
  private owners: ReadonlyMap<string, ShipSnapshot> = new Map();
  private receivedAt = 0;

  receive(
    shots: readonly ProjectileSnapshot[],
    owners: ReadonlyMap<string, ShipSnapshot>,
    now: number,
  ): void {
    this.owners = owners;
    this.receivedAt = now;
    const active = new Set(
      shots.filter((shot) => shot.kind === "laser").map((shot) => shot.id),
    );
    for (const [id, view] of this.views)
      if (view.active && !active.has(id)) {
        view.active = false;
        view.expiresAt = now + LASER_BEAM_LIMITS.afterglowMs;
      }
    this.trim(now);
    for (const shot of shots) {
      if (shot.kind !== "laser") continue;
      let view = this.views.get(shot.id);
      if (!view) {
        view = this.acquire(shot);
        this.reset(view, shot, owners.get(shot.ownerId));
        this.views.set(shot.id, view);
      } else if (
        view.shot.ownerId !== shot.ownerId ||
        Math.hypot(shot.x - view.shot.x, shot.y - view.shot.y) > 300
      ) {
        this.reset(view, shot, owners.get(shot.ownerId));
      } else {
        view.previous.x = view.active ? view.shot.x : shot.x;
        view.previous.y = view.active ? view.shot.y : shot.y;
        view.shot = shot;
        view.active = true;
      }
    }
  }

  render(
    now: number,
    viewport?: CombatViewport,
    renderedShips?: RenderedCombatShips,
  ): void {
    this.trim(now);
    const fraction = Math.max(0, Math.min(1, (now - this.receivedAt) / 100));
    const freshness = Math.max(
      0,
      1 -
        Math.max(0, now - this.receivedAt - LASER_BEAM_LIMITS.staleAfterMs) /
          LASER_BEAM_LIMITS.staleFadeMs,
    );
    for (const view of this.views.values()) {
      if (view.active) {
        view.end.x =
          view.previous.x + (view.shot.x - view.previous.x) * fraction;
        view.end.y =
          view.previous.y + (view.shot.y - view.previous.y) * fraction;
        this.positionOrigin(view, renderedShips);
      }
      const alpha =
        freshness *
        (view.active
          ? 1
          : Math.max(
              0,
              (view.expiresAt - now) / LASER_BEAM_LIMITS.afterglowMs,
            ));
      const dx = view.end.x - view.start.x,
        dy = view.end.y - view.start.y,
        length = Math.hypot(dx, dy);
      view.container.visible =
        alpha > 0.01 &&
        length > 2 &&
        laserBeamVisible(view.start, view.end, viewport);
      if (!view.container.visible) continue;
      view.container.position.set(view.start.x, view.start.y);
      view.container.rotation = Math.atan2(dy, dx);
      view.container.alpha = alpha;
      view.channel.width = length;
      view.channel.height = 8;
      view.muzzle.visible = view.active && view.attached;
    }
  }

  destroy(): void {
    this.container.destroy({ children: true });
    this.channelTexture.destroy(true);
    this.muzzleTexture.destroy(true);
    this.views.clear();
    this.spares.length = 0;
  }

  private positionOrigin(
    view: LaserView,
    renderedShips?: RenderedCombatShips,
  ): void {
    if (!view.owner) {
      view.start.x = view.end.x - Math.cos(view.shot.angle) * 90;
      view.start.y = view.end.y - Math.sin(view.shot.angle) * 90;
      return;
    }
    const owner = this.owners.get(view.shot.ownerId);
    const point = renderedShips?.get(view.shot.ownerId)?.container ?? owner;
    if (
      view.attached &&
      (!owner ||
        !point ||
        owner.dead ||
        owner.docked ||
        owner.hullId !== view.owner.hullId ||
        owner.sectorId !== view.owner.sectorId ||
        (owner.controls?.flightId ?? null) !== view.owner.flightId ||
        Math.hypot(point.x - view.owner.x, point.y - view.owner.y) > 300)
    )
      view.attached = false;
    if (!view.attached || !point) return;
    view.owner.x = point.x;
    view.owner.y = point.y;
    if (
      renderedShips?.get(view.shot.ownerId)?.weaponMuzzle?.(0, 1, view.start)
    ) {
      if (
        (view.end.x - view.start.x) * Math.cos(view.shot.angle) +
          (view.end.y - view.start.y) * Math.sin(view.shot.angle) >=
        2
      )
        return;
    }
    const dx = view.end.x - point.x,
      dy = view.end.y - point.y,
      distance = Math.hypot(dx, dy);
    const muzzle = Math.min(distance, view.owner.radius + 5);
    view.start.x = point.x + (distance > 0 ? (dx / distance) * muzzle : 0);
    view.start.y = point.y + (distance > 0 ? (dy / distance) * muzzle : 0);
  }

  private reset(
    view: LaserView,
    shot: ProjectileSnapshot,
    owner: ShipSnapshot | undefined,
  ): void {
    view.shot = shot;
    view.previous.x = shot.x;
    view.previous.y = shot.y;
    view.end.x = shot.x;
    view.end.y = shot.y;
    view.owner =
      owner && !owner.dead && !owner.docked
        ? {
            hullId: owner.hullId,
            sectorId: owner.sectorId,
            flightId: owner.controls?.flightId ?? null,
            x: owner.x,
            y: owner.y,
            radius: owner.radius,
          }
        : null;
    view.attached = view.owner !== null;
    view.active = true;
    view.expiresAt = Infinity;
    view.container.label = `laser:${shot.id}`;
    view.container.visible = false;
    this.positionOrigin(view);
  }

  private acquire(shot: ProjectileSnapshot): LaserView {
    const spare = this.spares.pop();
    if (spare) return spare;
    const container = new Container();
    const channel = new Sprite({
      texture: this.channelTexture,
      anchor: { x: 0, y: 0.5 },
      blendMode: "add",
      label: "laser-channel",
    });
    const muzzle = new Sprite({
      texture: this.muzzleTexture,
      anchor: 0.5,
      blendMode: "add",
      label: "laser-muzzle",
      alpha: 0.75,
    });
    muzzle.width = muzzle.height = 22;
    container.addChild(channel, muzzle);
    this.container.addChild(container);
    return {
      container,
      channel,
      muzzle,
      shot,
      previous: { x: shot.x, y: shot.y },
      start: { x: shot.x, y: shot.y },
      end: { x: shot.x, y: shot.y },
      owner: null,
      attached: false,
      active: true,
      expiresAt: Infinity,
    };
  }

  private trim(now: number): void {
    let fading = 0;
    for (const view of this.views.values()) if (!view.active) fading++;
    for (const [id, view] of this.views) {
      if (
        view.active ||
        (view.expiresAt > now && fading <= LASER_BEAM_LIMITS.afterglows)
      )
        continue;
      fading--;
      this.views.delete(id);
      view.container.visible = false;
      if (this.spares.length < LASER_BEAM_LIMITS.spares) this.spares.push(view);
      else view.container.destroy({ children: true });
    }
  }
}
